<template>
  <Teleport to="body">
    <div v-if="hasModals">
      <template v-for="(entry, idx) in stack" :key="entry.id">
        <!-- Custom modal component -->
        <component
          v-if="entry.component"
          :is="entry.component"
          v-bind="entry.props"
          :close-on-esc="entry.options?.closeOnEsc !== false && idx === stack.length - 1"
          :close-on-backdrop="entry.options?.closeOnBackdrop !== false && idx === stack.length - 1"
          :z-index="baseZ + idx"
          @close="(...args) => onCloseModal(entry.id, args.length > 0 ? args[0] : null)"
        />

        <!-- Default BaseModal -->
        <BaseModal
          v-else
          :title="entry.props?.title || ''"
          :close-on-esc="entry.options?.closeOnEsc !== false && idx === stack.length - 1"
          :close-on-backdrop="entry.options?.closeOnBackdrop !== false && idx === stack.length - 1"
          :modal-width="entry.props?.modalWidth"
          :z-index="baseZ + idx"
          @close="onCloseModal(entry.id, null)"
        >
          <div v-if="entry.props?.message" class="wysiwyg" :class="entry.props.contentClass" v-html="entry.props.message"></div>

          <template #actions>
            <button
              type="button"
              class="btn btn--dark-green"
              :class="{ 'btn-red': !!entry.props?.danger }"
              v-if="entry.props?.confirmText !== false"
              @click="onCloseModal(entry.id, true)"
            >
              {{ entry.props?.confirmText || __('Okay') }}
            </button>

            <button
              type="button"
              class="btn btn--white-border"
              v-if="entry.props?.cancelText"
              @click="onCloseModal(entry.id, false)"
            >
              {{ entry.props.cancelText }}
            </button>
          </template>
        </BaseModal>
      </template>
    </div>
  </Teleport>
</template>

<script>
import LocalizationMixin from '@assets/js/vue-components/LocalizationMixin.js';
import BaseModal from './BaseModal.vue'
import { useModal } from './useModal'
import { disableBodyScroll, enableBodyScroll } from '@assets/js/body-scroll-lock.js';

export default {
  name: 'ModalManager',

  mixins: [
    LocalizationMixin,
  ],

  components: {
    BaseModal
  },

  data() {
    return {
      baseZ: 20,
      _scrollLockCount: 0,
      _prevOverflow: null,
    }
  },

  computed: {
    modal() {
      return useModal()
    },

    stack() {
      return this.modal.state.stack
    },

    hasModals() {
      return this.stack && this.stack.length > 0
    }
  },

  watch: {
    hasModals: {
      immediate: true,
      handler(val) {
        if (val) {
          this.lockScroll()
        } else {
          this.unlockScroll()
        }
      }
    }
  },

  beforeUnmount() {
    this.unlockScroll(true)
  },

  methods: {
    lockScroll() {
      if (this._scrollLockCount === 0) {
        disableBodyScroll();
      }
      this._scrollLockCount += 1
    },

    unlockScroll(force = false) {
      if (force) {
        this._scrollLockCount = 0
      } else if (this._scrollLockCount > 0) {
        this._scrollLockCount -= 1
      }

      if (this._scrollLockCount === 0) {
        enableBodyScroll();
      }
    },

    onCloseModal(id, result) {
      const entry = this.stack.find(m => m.id === id)
      if (!entry) {
        return
      }

      this.modal.close(id)

      if (entry.resolve) {
        // If it's a confirm modal, the result can be one of the following
        //   true - The user clicked the confirm button
        //   false - The user clicked the cancel button
        //   null - The user clicked the close button or outside the modal
        //   object - Custom data from a modal component

        if (result === null) {
          if (entry.defaultResult) {
            result = entry.defaultResult
          } else {
            result = false
          }
        }

        entry.resolve(result)
      }
    }
  }
}
</script>
